import React from 'react';
import { Snackbar, Alert, Slide, Grow, Fade } from '@mui/material';
import { useThemeColors } from '../hooks/useThemeColors';

/**
 * Componente per la transizione slide-up delle notifiche
 */
const SlideUpTransition = (props) => {
    return <Slide {...props} direction="up" />;
};

/**
 * Componente per la transizione grow delle notifiche
 */
const GrowTransition = (props) => {
    return <Grow {...props} />;
};

/**
 * Componente per la transizione fade delle notifiche
 */
const FadeTransition = (props) => {
    return <Fade {...props} />;
};

/**
 * Componente standardizzato per la visualizzazione delle notifiche
 * Supporta tutti i tipi di notifica: success, error, warning, info
 */
export const NotificationSnackbar = ({
    notification,
    onClose,
    type = 'success',
    position = { vertical: 'bottom', horizontal: 'left' },
    transition = 'slide',
    variant = 'filled',
    elevation = 6,
    autoHideDuration = null // Se null, usa la duration della notifica
}) => {
    const themeColors = useThemeColors();
    
    // Seleziona la transizione appropriata
    const getTransitionComponent = () => {
        switch (transition) {
            case 'grow':
                return GrowTransition;
            case 'fade':
                return FadeTransition;
            case 'slide':
            default:
                return SlideUpTransition;
        }
    };

    // Determina la duration effettiva
    const effectiveDuration = autoHideDuration !== null 
        ? autoHideDuration 
        : notification?.duration || 6000;

    // Colori personalizzati per il tema
    const getAlertColors = () => {
        const colors = themeColors.semantic;
        switch (type) {
            case 'success':
                return {
                    backgroundColor: colors.success.background,
                    color: colors.success.text,
                    border: `1px solid ${colors.success.border}`
                };
            case 'error':
                return {
                    backgroundColor: colors.error.background,
                    color: colors.error.text,
                    border: `1px solid ${colors.error.border}`
                };
            case 'warning':
                return {
                    backgroundColor: colors.warning.background,
                    color: colors.warning.text,
                    border: `1px solid ${colors.warning.border}`
                };
            case 'info':
                return {
                    backgroundColor: colors.info.background,
                    color: colors.info.text,
                    border: `1px solid ${colors.info.border}`
                };
            default:
                return {};
        }
    };

    return (
        <Snackbar
            open={Boolean(notification)}
            autoHideDuration={effectiveDuration}
            onClose={onClose}
            anchorOrigin={position}
            TransitionComponent={getTransitionComponent()}
            sx={{
                '& .MuiSnackbarContent-root': {
                    minWidth: 300,
                    maxWidth: 600
                }
            }}
        >
            <Alert
                onClose={onClose}
                severity={type}
                variant={variant}
                elevation={elevation}
                sx={{
                    width: '100%',
                    ...(variant === 'filled' ? getAlertColors() : {}),
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    '& .MuiAlert-icon': {
                        fontSize: '1.2rem'
                    }
                }}
            >
                {notification?.message || notification}
            </Alert>
        </Snackbar>
    );
};

/**
 * Componente per visualizzare multiple notifiche simultanee
 */
export const MultipleNotifications = ({
    notifications = [],
    onClose,
    spacing = 60, // Spazio tra le notifiche in pixel
    maxVisible = 3 // Numero massimo di notifiche visibili simultaneamente
}) => {
    // Mostra solo le ultime N notifiche
    const visibleNotifications = notifications.slice(-maxVisible);

    return (
        <>
            {visibleNotifications.map((notification, index) => (
                <NotificationSnackbar
                    key={notification.timestamp || index}
                    notification={notification}
                    type={notification.type}
                    onClose={() => onClose(notification.type)}
                    position={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    sx={{
                        bottom: `${spacing * (index + 1)}px !important`
                    }}
                />
            ))}
        </>
    );
};

/**
 * Componente wrapper che gestisce automaticamente le notifiche
 * da un hook useNotifications
 */
export const NotificationProvider = ({
    notifications,
    onClear,
    children,
    position = { vertical: 'bottom', horizontal: 'left' },
    transition = 'slide',
    variant = 'filled',
    maxVisible = 3
}) => {
    const activeNotifications = notifications.activeNotifications || [];

    return (
        <>
            {children}
            <MultipleNotifications
                notifications={activeNotifications}
                onClose={onClear}
                maxVisible={maxVisible}
            />
            
            {/* Notifiche individuali per compatibilità */}
            {notifications.notification?.success && (
                <NotificationSnackbar
                    notification={notifications.notification.success}
                    type="success"
                    onClose={() => onClear('success')}
                    position={position}
                    transition={transition}
                    variant={variant}
                />
            )}
            
            {notifications.notification?.error && (
                <NotificationSnackbar
                    notification={notifications.notification.error}
                    type="error"
                    onClose={() => onClear('error')}
                    position={position}
                    transition={transition}
                    variant={variant}
                />
            )}
            
            {notifications.notification?.warning && (
                <NotificationSnackbar
                    notification={notifications.notification.warning}
                    type="warning"
                    onClose={() => onClear('warning')}
                    position={position}
                    transition={transition}
                    variant={variant}
                />
            )}
            
            {notifications.notification?.info && (
                <NotificationSnackbar
                    notification={notifications.notification.info}
                    type="info"
                    onClose={() => onClear('info')}
                    position={position}
                    transition={transition}
                    variant={variant}
                />
            )}
        </>
    );
};

/**
 * Componente semplificato per notifiche base
 */
export const SimpleNotificationSnackbars = ({
    error,
    success,
    onClearError,
    onClearSuccess,
    successMessage = "Operazione completata con successo!",
    position = { vertical: 'bottom', horizontal: 'left' }
}) => {
    return (
        <>
            <NotificationSnackbar
                notification={error}
                type="error"
                onClose={onClearError}
                position={position}
            />
            
            <NotificationSnackbar
                notification={success ? successMessage : null}
                type="success"
                onClose={onClearSuccess}
                position={position}
            />
        </>
    );
};

export default NotificationSnackbar;